<template>
  <div
    v-if="show"
    ref="popup"
    :class="$.vgp_date_range_picker"
    :style="{...position}"
  >
    <div
      v-if="showDateRangePresets"
      :class="$.header"
    >
      <div
        v-for="(item, index) in currentPresets"
        :key="index"
        :class="[$.header__item, currentPreset === item ? $.active : '']"
        :data-text="locales(`datepiker_${item}`)"
        @click="setPreset(item)"
      >
        <span :class="$.text">{{ locales(`datepiker_${item}`) }}</span>
      </div>
    </div>
    <div
      v-else
      :style="{height: '10px'}"
    />
    <vgp-date-picker
      ref="calendar"
      :is-range="isRange"
      :custom-selector="true"
      :value="rangeValue.start"
      :range-value="rangeValue"
      :columns="2"
      :min-date="minDate"
      :max-date="maxDate"
      :has-default-slot="false"
      :has-footer-slot="false"
      @onChange="onChange"
    />
    <div :class="$.footer">
      <vgp-button
        type="primary"
        :small="true"
        :label="locales('common_done')"
        @onClick="hidePicker"
      />
    </div>
  </div>
</template>

<script>
import _ from '../../../../libs/lodash';
import moment from '../../../../libs/moment';
import vgpDatePicker from '../datePicker/vgpDatePicker.vue';
import vgpButton from '../globalButton/vgpButton.vue';
import constants from '../../../../helpers/constants';

const defaultPosition = {
  top: '-1000px', bottom: 'auto', left: '-1000px', right: 'auto',
};
const monthPresets = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month'];
const yearPresets = ['this_year', 'this_quarter', 'last_quarter', 'last_6_months', 'last_12_months'];
const format = 'YYYY-MM-DD HH:mm:ss';
const emptyRange = { start: null, end: null };

function getRangeByPresetName(name) {
  let start;
  let end;

  const startOfWeek = +user.settings.start_monday ? 'isoWeek' : 'week';

  switch (name) {
  case 'today':
    start = moment().startOf('day').format(format);
    end = moment().endOf('day').format(format);
    break;

  case 'yesterday':
    start = moment().subtract(1, 'day').startOf('day').format(format);
    end = moment().subtract(1, 'day').endOf('day').format(format);
    break;

  case 'this_week':
    start = moment().startOf(startOfWeek).format(format);
    end = moment().endOf(startOfWeek).format(format);
    break;

  case 'last_week':
    start = moment().subtract(1, 'week').startOf(startOfWeek).format(format);
    end = moment().subtract(1, 'week').endOf(startOfWeek).format(format);
    break;

  case 'this_month':
    start = moment().startOf('month').format(format);
    end = moment().endOf('month').format(format);
    break;

  case 'last_month':
    start = moment().subtract(1, 'month').startOf('month').format(format);
    end = moment().subtract(1, 'month').endOf('month').format(format);
    break;

  case 'this_year':
    start = moment().startOf('year').format(format);
    end = moment().endOf('day').format(format);
    break;

  case 'this_quarter':
    start = moment().startOf('quarter').format(format);
    end = moment().endOf('day').format(format);
    break;

  case 'last_quarter':
    start = moment().subtract(1, 'quarter').startOf('quarter').format(format);
    end = moment().subtract(1, 'quarter').endOf('quarter').format(format);
    break;

  case 'last_6_months':
    // start = moment().subtract(6, 'months').startOf('month').format(format);
    // end = moment().endOf('day').format(format);
    // break;
    start = moment().subtract(6, 'months').startOf('day').format(format);
    end = moment().endOf('day').format(format);
    break;

  case 'last_12_months':
    start = moment().subtract(12, 'months').startOf('month').format(format);
    end = moment().endOf('day').format(format);
    break;

  default:
    start = moment().startOf('day').format(format);
    end = moment().endOf('day').format(format);
  }

  return { start, end };
}

export default {
  name: 'VgpDateRangePicker',
  components: { vgpDatePicker, vgpButton },
  props: {
    show: { type: Boolean, required: true, default: false },
    showDateRangePresets: { type: Boolean, required: false, default: true },
    targetElement: { type: Object, required: true, default: null },
    parentElement: { type: Object, required: true, default: null },
    minDate: { type: Object, required: false, default: undefined },
    maxDate: { type: Object, required: false, default: undefined },
    rangeData: {
      type: Object,
      required: true,
      default: () => ({
        start: null,
        end: null,
        period: '',
      }),
    },
    presetsType: { type: String, required: false, default: 'month' },
    isRange: { type: Boolean, required: true, default: false },
  },
  emits: {
    onChange: Object,
    onClose: null,
  },
  data() {
    return {
      locales: __,
      currentPreset: null,
      rangeValue: null,
      position: defaultPosition,
      headerItemWidth: 0,
    };
  },
  computed: {
    currentPresets() {
      if (this.presetsType === 'year') {
        return yearPresets;
      }

      return monthPresets;
    },
    // defaultPreset() {
    //   const defaultRange = getRangeByPresetName('today');
    //   const { start, end } = defaultRange;

    //   return { start, end, period: this.presetsType === 'year' ? null : 'today' };
    // },
  },
  watch: {
    show: {
      handler(flag) {
        if (flag) {
          setTimeout(() => this.calcStyles(), 0);
        } else {
          this.position = defaultPosition;
        }
      },
      immediate: true,
    },
    rangeData: {
      handler(data) {
        if (data) {
          // this.setRange(data);
        } else {
          this.resetRange();
        }
      },
    },
  },
  mounted() {
    this.calcStyles();
    this.addListeners();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  created() {
    if (this.rangeData && Object.values(this.rangeData).some(value => value && value.length)) {
      this.setRange(this.rangeData);
    } else {
      this.resetRange();
    }
  },
  methods: {
    addListeners() {
      const divs = document.querySelectorAll('div.gantt_scroll');

      divs.forEach(div => {
        div.addEventListener('scroll', this.hidePicker);
      });
    },
    removeListeners() {
      const divs = document.querySelectorAll('div.gantt_scroll');

      divs.forEach(div => {
        div.removeEventListener('scroll', this.hidePicker);
      });
    },
    setRange(range) {
      if (range.period && this.currentPresets.includes(range.period) && this.showDateRangePresets) {
        this.setPreset(range.period);
      } else {
        this.rangeValue = {
          start: range.start,
          end: range.end,
        };
      }
    },
    resetRange() {
      this.currentPreset = null;
      this.rangeValue = {
        start: null,
        end: null,
      };
      this.$emit('onChange', '');
    },
    calcStyles() {
      if (!this.targetElement) return;

      const targetCoords = this.targetElement.getBoundingClientRect();

      let currentPosition = {
        top: targetCoords.top,
        left: targetCoords.left,
        right: targetCoords.right,
        bottom: targetCoords.bottom,
        width: targetCoords.width,
        height: targetCoords.height,
      };

      if (this.parentElement) {
        const parentCoords = this.parentElement.getBoundingClientRect();

        currentPosition = {
          ...currentPosition,
          top: currentPosition.top - parentCoords.top,
          bottom: currentPosition.bottom - parentCoords.top,
          left: currentPosition.left - parentCoords.left,
          right: currentPosition.left + currentPosition.width,
        };
      }

      const popup = this.$refs.popup;
      const bottomDiff = document.documentElement.clientHeight - targetCoords.bottom;
      const rightDiff = document.documentElement.clientWidth - targetCoords.left;

      let position = {};

      if (bottomDiff < popup.clientHeight) {
        position = {
          ...defaultPosition,
          top: `${currentPosition.top - popup.clientHeight - 2}px`,
        };
      } else {
        position = {
          ...defaultPosition,
          top: `${currentPosition.bottom + 2}px`,
        };
      }

      if (rightDiff < popup.clientWidth) {
        position = {
          ...position,
          left: `${currentPosition.left - (popup.clientWidth - currentPosition.width)}px`,
        };
      } else {
        position = {
          ...position,
          left: `${currentPosition.left}px`,
        };
      }

      if (targetCoords.top < popup.clientHeight && bottomDiff < popup.clientHeight) {
        position = {
          ...defaultPosition,
          top: 0,
        };

        if (rightDiff < popup.clientWidth) {
          position = {
            ...position,
            right: `${document.documentElement.clientWidth - currentPosition.left}px`,
            left: 'auto',
          };
        }
      }

      if (this.headerItemWidth === 0) {
        this.headerItemWidth = `${(popup.clientWidth - 32) / this.currentPresets.length}px`;
      }

      this.position = position;
    },
    setPreset(preset) {
      const calendar = this.$refs.calendar;
      const currentDate = new Date();
      const previousMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const rangeByPreset = getRangeByPresetName(preset);

      const { start, end } = rangeByPreset;

      if (calendar) {
        if (preset === 'last_month') {
          calendar.move(previousMonthDate);
        } else {
          calendar.move(currentDate);
        }
      }

      const newRange = { start, end, period: preset };

      if (!this.isEqual(newRange)) {
        this.rangeValue = newRange;
        this.currentPreset = preset;
      }
    },
    onChange(value) {
      if (this.isRange) {
        const newStartDate = moment(value.start).startOf('day').format(format);
        const newEndDate = moment(value.end).endOf('day').format(format);
        const newRange = {
          ...value,
          start: newStartDate,
          end: newEndDate,
        };

        if (!this.isEqual(newRange)) {
          const todayRange = getRangeByPresetName('today');
          const yesterdayRange = getRangeByPresetName('yesterday');
          const isToday = newStartDate === todayRange.start && newEndDate === todayRange.end;
          const isYesterday = newStartDate === yesterdayRange.start && newEndDate === yesterdayRange.end;

          if (this.currentPreset) {
            const presetRange = getRangeByPresetName(this.currentPreset);
            const isCurrent = newStartDate === presetRange.start && newEndDate === presetRange.end;

            if (!isCurrent) {
              this.currentPreset = null;
            }
          }

          if (isToday) {
            this.currentPreset = 'today';
          }

          if (isYesterday) {
            this.currentPreset = 'yesterday';
          }

          this.$emit('onChange', {
            ...newRange,
            period: this.currentPreset,
          });
          this.rangeValue = newRange;
        }
      } else {
        const date = moment(value).locale('en').endOf('day').format(format);

        this.$emit('onChange', date);
      }
    },
    hidePicker() {
      this.$emit('onClose');
    },
    isEqual(newRange) {
      if (newRange.period && this.rangeValue?.period === newRange.period) {
        return true;
      }

      return _.isEqual(this.rangeValue, newRange);
    },
  },
};
</script>

<style module='$' lang='less'>
@import '../../../../less/_fonts.less';
@import '../../../../less/_mixins.less';

.vgp_date_range_picker {
  width: 500px;
  position: fixed;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0px 9px 22px -2px rgba(0, 0, 0, 0.2);
  z-index: 2;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    padding-bottom: 0px;

    &__item {
      position: relative;
      padding: 0 4px;
      cursor: pointer;

      &::after {
        content: attr(data-text);
        .FontLatoBold;
        .fs(12px, 26px);
        visibility: hidden;
        display: block;
        height: 0;
        overflow: hidden;
      }

      .text {
        .FontLatoRegular;
        .fs(12px, 26px);
        color: #808080;
        white-space: nowrap;
      }

      &.active {
        .text {
          .FontLatoBold;
          color: #1565C0;
        }
      }

      &:not(.active):hover {
        .text {
          .FontLatoBold;
          color: #191919;
        }
      }
    }
  }

  .slot_line {
    height: 1px;
    width: 100%;
    background: #e5e5e5;
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    padding: 16px 20px;
    padding-top: 4px;
  }
}
</style>
